/*!
 * Cart Group Guard – Device + Subscription
 * Keeps paired device and subscription lines in sync within each group.
 * Groups are identified by the `_group_id` property.
 * Types: `_type: 'device'` and `_type: 'subscription'`.
 *
 * Guarantees:
 *   - Sequential (not parallel) cart changes → no dropped requests.
 *   - Both lines of a group always get the same quantity.
 *   - Orphan groups (only device OR only subscription) are auto-deleted.
 *   - A component device bought on its own — no selling plan on the line and
 *     no _group_id — is deleted at ANY price. A paid device-only order is
 *     just as unacceptable as a $0 one. Component lines are matched by
 *     handle (any price) or by SKU prefix ($0 lines only).   ← NEW
 *   - The founder gift without a paid subscription is deleted.
 *   - A group is only valid while one of its lines really carries a selling
 *     plan, so a forged _group_id/_type pair (a /cart permalink can inject
 *     those properties) is deleted as an orphan group.      ← NEW
 *   - Checkout is blocked until the first cleanup finishes. ← NEW
 *   - On page load and after every cart update, cleanups are swept.
 *   - Retry-once if the final cart state doesn't match the intent.
 */
(function () {
  'use strict';

  if (window.__CART_GROUP_GUARD__) return;
  window.__CART_GROUP_GUARD__ = true;

  var GROUP_ID = '_group_id';
  var TYPE = '_type';
  var DEVICE_TYPE = 'device';
  var SUB_TYPE = 'subscription';
  var DEVICE_TYPES = ['device', 'ownership_device'];
  var SUB_TYPES = ['subscription', 'filter_subscription'];

  // ---- Protected products (edit as catalog changes) -------------------
  // A line matching one of these handles is a component device. It may only
  // live in the cart while it is attached to a selling plan — either on its
  // own line (the single-product-purchase flow used by /products/nano,
  // /products/model-1 and /products/sparkle) or inside a _group_id group.
  // A bare component line is removed no matter what it costs: the device
  // must never be ordered on its own (Task 289).
  var PROTECTED_DEVICE_HANDLES = [
    'new-wisewell-sparkle-subscription',
    'new-wisewell-nano-subscription',
    'new-wisewell-model-1-subscription',
    // Older alias handles — still reachable (see snippets/products-redirect).
    'wisewell-sparkle-subscription',
    'wisewell-nano-subscription',
    'wisewell-model-1-subscription',
    // Task 290 — the three PDPs that own the /products/* URLs are the same
    // $0 machine lines (the handles above were the pre-launch names). A $0
    // line for one of these without a _group_id is a broken component line
    // and must be removed, exactly like its legacy handle.
    'nano',
    'model-1',
    'sparkle'
  ];
  // Task 289 — the components are also identified by SKU prefix, so a handle
  // rename or a brand-new alias cannot slip a component through.
  // The SKU path only ever fires for $0 lines (see isRemovableComponent), so
  // the paid ownership devices — which share the SPK-120-/NNT-110-/MDO-110-
  // SKU family — can never be removed by this. They also carry a selling plan
  // or a _group_id, which keeps them out of the sweep on its own.
  var COMPONENT_SKU_PREFIXES = ['SPK-120-', 'NNT-110-', 'MDO-110-'];
  // Founder gift is only valid when the cart contains a paid subscription.
  var FOUNDER_GIFT_HANDLE = 'gift-product';
  var FOUNDER_GIFT_SKU = 'MSC-GFT-002';
  // ---------------------------------------------------------------------

  var isOnCartPage = window.location.pathname.indexOf('/cart') !== -1;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function url(path) {
    return root + path.replace(/^\/+/, '');
  }

  // ---------- Cart API ----------
  function getCart() {
    return fetch(url('cart.js'), {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) { return r.json(); });
  }

  function changeLine(identifier, quantity, byIndex) {
    var payload = byIndex
      ? { line: identifier, quantity: quantity }
      : { id: identifier, quantity: quantity };
    return fetch(url('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) throw new Error(data.description || data.message || 'Unable to update cart.');
        return data;
      });
    });
  }

  // ---------- Helpers ----------
  function getGroupId(item) {
    return item && item.properties && item.properties[GROUP_ID] || null;
  }
  function getType(item) {
    return item && item.properties && item.properties[TYPE] || null;
  }
  function isDevice(item) {
    return DEVICE_TYPES.indexOf(getType(item)) !== -1;
  }
  function isSubscription(item) {
    return SUB_TYPES.indexOf(getType(item)) !== -1;
  }
  // Does the line itself really carry a selling plan? Shopify sets
  // `selling_plan_allocation` server-side, so — unlike _group_id/_type/
  // properties, which a /cart permalink can inject — it cannot be forged.
  function hasSellingPlan(item) {
    return !!(item && (item.selling_plan_allocation || item.selling_plan_id));
  }
  // Task 289 — is this line a component device (matched by handle or SKU)?
  // Handle match first (fast), SKU prefix second (rename-proof).
  function isProtectedHandle(item) {
    if (!item) return false;
    return PROTECTED_DEVICE_HANDLES.indexOf(item.handle || '') !== -1;
  }
  function isComponentSku(item) {
    var sku = (item && item.sku ? item.sku : '').toUpperCase();
    if (!sku) return false;
    for (var i = 0; i < COMPONENT_SKU_PREFIXES.length; i++) {
      if (sku.indexOf(COMPONENT_SKU_PREFIXES[i]) === 0) return true;
    }
    return false;
  }
  // A component line that is in the cart without a selling plan and without a
  // _group_id is a device-only order and must go:
  //   - allow-listed handles match at ANY price, because a paid device-only
  //     order is just as unacceptable as a $0 one;
  //   - the SKU path only matches $0 lines, so a paid ownership device sharing
  //     the same SKU family is never deleted by accident.
  function isRemovableComponent(item) {
    if (!item) return false;
    if (isProtectedHandle(item)) return true;
    return isComponentSku(item) && item.final_price === 0;
  }
  function isFounderGiftLine(item) {
    if (!item) return false;
    if ((item.handle || '') === FOUNDER_GIFT_HANDLE) return true;
    return (item.sku || '').toUpperCase() === FOUNDER_GIFT_SKU;
  }

  // ---------- State ----------
  var groups = {};
  var groupKeys = new Set();
  var variantIdToKey = {};
  var indexToKey = {};
  var stateLoaded = false;

  var rawItems = [];             // ← NEW: last-known cart.items, untouched
  var cleanupComplete = false;   // ← NEW: has the first cleanup finished?
  var pendingCheckout = null;    // ← NEW: checkout element awaiting cleanup

  function refreshFromCart(cart) {
    rawItems = (cart && cart.items) || [];   // ← NEW

    groups = {};
    groupKeys = new Set();
    variantIdToKey = {};
    indexToKey = {};

    (cart.items || []).forEach(function (item, index) {
      variantIdToKey[item.id] = item.key;
      if (item.variant_id) variantIdToKey[item.variant_id] = item.key;
      indexToKey[index + 1] = item.key;

      var gid = getGroupId(item);
      if (!gid) return;

      if (!groups[gid]) {
        groups[gid] = {
          deviceKey: null,
          subscriptionKey: null,
          deviceKeys: [],
          subscriptionKeys: [],
          deviceQty: 0,
          subscriptionQty: 0,
          // ← NEW: does any line of this group carry a real selling plan?
          hasSellingPlan: false,
          keys: new Set()
        };
      }

      var group = groups[gid];
      group.keys.add(item.key);
      groupKeys.add(item.key);
      if (hasSellingPlan(item)) group.hasSellingPlan = true;

      if (isDevice(item)) {
        group.deviceKey = item.key;
        group.deviceKeys.push(item.key);
        group.deviceQty = item.quantity;
      } else if (isSubscription(item)) {
        group.subscriptionKey = item.key;
        group.subscriptionKeys.push(item.key);
        group.subscriptionQty = item.quantity;
      }
    });
    stateLoaded = true;
  }

  function refreshFromServer() {
    return getCart().then(refreshFromCart).catch(function () {});
  }

  // ---------- Loader ----------
  var loader = null;

  function ensureStyles() {
    if (document.getElementById('cart-group-loader-style')) return;
    var style = document.createElement('style');
    style.id = 'cart-group-loader-style';
    style.textContent =
      '.cart-group-loader{position:fixed;top:0;left:0;width:100%;height:100%;z-index:99999;' +
      'background:rgba(255,255,255,.55);display:flex;align-items:center;justify-content:center;' +
      'flex-direction:column;gap:12px;font-family:inherit;font-size:15px;color:#333}' +
      '.cart-group-loader__spinner{width:34px;height:34px;border:3px solid rgba(0,0,0,.15);' +
      'border-top-color:#000;border-radius:50%;animation:cartGroupSpin .7s linear infinite}' +
      '@keyframes cartGroupSpin{to{transform:rotate(360deg)}}';
    document.head.appendChild(style);
  }

  function showLoader() {
    if (loader) return;
    ensureStyles();
    loader = document.createElement('div');
    loader.className = 'cart-group-loader';
    loader.setAttribute('role', 'status');
    loader.setAttribute('aria-live', 'polite');
    loader.innerHTML = '<span class="cart-group-loader__spinner"></span><span>Updating your cart…</span>';
    document.body.appendChild(loader);
  }

  function hideLoader() {
    if (loader) {
      if (loader.parentNode) loader.parentNode.removeChild(loader);
      loader = null;
    }
  }

  // ---------- Orphan helpers ----------
  // A group is orphaned when it lost one half — or when none of its lines
  // actually carries a selling plan. The plan check closes a /cart permalink
  // attack that can inject _group_id/_type on two plan-less lines to fake a
  // complete bundle; Shopify-set selling plans cannot be forged that way.
  function findOrphanGroupIds() {
    var out = [];
    for (var gid in groups) {
      var g = groups[gid];
      var deviceLive = !!(g.deviceKey && g.deviceQty > 0);
      var subLive = !!(g.subscriptionKey && g.subscriptionQty > 0);
      if (deviceLive !== subLive) {
        out.push(gid);
        continue;
      }
      if ((deviceLive || subLive) && !g.hasSellingPlan) out.push(gid);
    }
    return out;
  }

  // ← NEW: find lines that should never be in the cart on their own.
  // Returns an array of line keys to remove.
  function findUntaggedViolations() {
    var violations = [];
    if (!rawItems.length) return violations;

    // Does the cart contain any paid subscription line at all?
    // Count both `_type`-tagged lines and any line carrying a selling plan,
    // so a paid plan added by another path still protects the gift.
    var paidSubCount = rawItems.filter(function (it) {
      var tagged = SUB_TYPES.indexOf(getType(it)) !== -1;
      var hasPlan = !!it.selling_plan_allocation;
      return (tagged || hasPlan) && it.final_price > 0;
    }).length;

    rawItems.forEach(function (it) {
      // Untagged component device with no selling plan of its own → a
      // device-only order (paid or $0), remove it.
      if (!getGroupId(it) && !hasSellingPlan(it) && isRemovableComponent(it)) {
        violations.push(it.key);
        return;
      }

      // Founder gift with no paid subscription anywhere in the cart.
      if (isFounderGiftLine(it) && paidSubCount === 0) {
        violations.push(it.key);
      }
    });

    return violations;
  }

  function deleteGroupLines(groupId) {
    var g = groups[groupId];
    if (!g) return Promise.resolve(null);

    var subKeys = g.subscriptionKeys.slice();
    var deviceKeys = g.deviceKeys.slice();

    if (g.subscriptionKey && subKeys.indexOf(g.subscriptionKey) === -1) subKeys.push(g.subscriptionKey);
    if (g.deviceKey && deviceKeys.indexOf(g.deviceKey) === -1) deviceKeys.push(g.deviceKey);

    var chain = Promise.resolve();
    subKeys.forEach(function (k) {
      chain = chain.then(function () {
        return changeLine(k, 0).catch(function () {});
      });
    });
    deviceKeys.forEach(function (k) {
      chain = chain.then(function () {
        return changeLine(k, 0).catch(function () {});
      });
    });

    return chain.then(function () { return getCart(); });
  }

  // ← UPDATED: now also removes untagged violations, and marks the
  // cleanup as complete on both success and failure (fail-open — the
  // server-side Checkout Validation Function is the real guard).
  function cleanupOrphans() {
    return getCart().then(function (cart) {
      refreshFromCart(cart);

      var orphans = findOrphanGroupIds();
      var untagged = findUntaggedViolations();

      if (!orphans.length && !untagged.length) {
        cleanupComplete = true;
        return null;
      }

      // Snapshot the pre-sweep line keys. Used afterwards to prove the sweep
      // really changed the cart before repainting the page.
      var beforeKeys = rawItems.map(function (it) { return it.key; }).join(',');

      var chain = Promise.resolve();

      // Untagged removals first (no group bookkeeping needed).
      untagged.forEach(function (key) {
        var victim = null;
        rawItems.some(function (it) {
          if (it.key !== key) return false;
          victim = it;
          return true;
        });
        console.warn(
          '[Cart Group] Removed "' + (victim ? (victim.handle || victim.title || key) : key) + '" — ' +
          (victim && isFounderGiftLine(victim)
            ? 'founder gift without a paid subscription.'
            : 'device/component line with no selling plan and no _group_id.')
        );
        chain = chain.then(function () {
          return changeLine(key, 0).catch(function () {});
        });
      });

      // Then whole orphan groups.
      orphans.forEach(function (gid) {
        chain = chain.then(function () { return deleteGroupLines(gid); });
      });

      return chain.then(function () {
        return getCart().then(function (c2) {
          refreshFromCart(c2);
          cleanupComplete = true;

          // The sweep changed the cart. On the cart page the rendered rows
          // still show the deleted lines, so those rows are now stale and
          // their (x) buttons 404. Repaint. Guarded by a before/after
          // comparison so a line that refuses to go cannot reload-loop.
          var afterKeys = rawItems.map(function (it) { return it.key; }).join(',');
          if (afterKeys !== beforeKeys) reloadCartPage(false);

          return c2;
        });
      });
    }).catch(function (err) {
      console.warn('[Cart Group] Cleanup failed:', err);
      // Fail-open: let checkout proceed; the server function blocks bad orders.
      cleanupComplete = true;
      return null;
    });
  }

  // ---------- Apply a target quantity to both lines of a group ----------
  function applyGroupQuantity(groupId, qty) {
    qty = Math.max(0, parseInt(qty, 10) || 0);

    return getCart().then(function (cart) {
      refreshFromCart(cart);
      return runApply();
    }).catch(function (err) {
      console.warn('[Cart Group] Could not read cart:', err);
      return null;
    });

    function runApply() {
      var group = groups[groupId];
      if (!group) return Promise.resolve(null);

      var deviceLive = !!(group.deviceKey && group.deviceQty > 0);
      var subLive = !!(group.subscriptionKey && group.subscriptionQty > 0);

      if (deviceLive !== subLive) {
        return deleteGroupLines(groupId)
          .then(function (newCart) {
            refreshFromCart(newCart);
            return finalize(newCart);
          })
          .catch(function (err) {
            hideLoader();
            console.warn('[Cart Group] Orphan delete failed:', err);
            return null;
          });
      }

      if (!deviceLive && !subLive) return Promise.resolve(null);

      function stepUpdateDevice() {
        if (group.deviceQty === qty) return Promise.resolve();
        return changeLine(group.deviceKey, qty);
      }
      function stepUpdateSub() {
        if (group.subscriptionQty === qty) return Promise.resolve();
        return changeLine(group.subscriptionKey, qty);
      }

      function sequence() {
        if (qty === 0) return stepUpdateSub().then(stepUpdateDevice);
        return stepUpdateDevice().then(stepUpdateSub);
      }

      return sequence()
        .then(function () { return getCart(); })
        .then(function (cart1) {
          refreshFromCart(cart1);

          var g = groups[groupId];
          if (!g) return cart1;

          var stillDeviceLive = !!(g.deviceKey && g.deviceQty > 0);
          var stillSubLive = !!(g.subscriptionKey && g.subscriptionQty > 0);

          if (qty === 0) {
            if (stillDeviceLive || stillSubLive) {
              return deleteGroupLines(groupId).then(function (c2) {
                refreshFromCart(c2);
                return c2;
              });
            }
            return cart1;
          }

          if (stillDeviceLive !== stillSubLive) {
            return deleteGroupLines(groupId).then(function (c2) {
              refreshFromCart(c2);
              return c2;
            });
          }

          if (g.deviceQty !== qty || g.subscriptionQty !== qty) {
            var chain = Promise.resolve();
            if (g.deviceQty !== qty) {
              chain = chain.then(function () { return changeLine(g.deviceKey, qty); });
            }
            if (g.subscriptionQty !== qty) {
              chain = chain.then(function () { return changeLine(g.subscriptionKey, qty); });
            }
            return chain.then(function () { return getCart(); }).then(function (c2) {
              refreshFromCart(c2);
              return c2;
            });
          }

          return cart1;
        })
        .then(function (finalCart) { return finalize(finalCart); })
        .catch(function (err) {
          console.warn('[Cart Group] Change failed:', err);
          if (!reloadCartPage(true)) {
            hideLoader();
            refreshFromServer();
          }
          return null;
        });
    }
  }

  // ---- Repaint the cart page -------------------------------------------
  // On the cart page the rows are server-rendered HTML. Removing a line
  // through the Cart API does NOT update them, so the customer is left
  // looking at rows whose `data-line-key` no longer exists — every later
  // click then 404s and the (x) button looks dead. The only reliable repaint
  // is a reload. `force` is used for a customer's own click; the automatic
  // sweep is rate-limited so an app that keeps re-adding a line we keep
  // deleting cannot turn into a reload loop.
  var REPAINT_KEY = 'cartGroupRepaintAt';
  var REPAINT_MIN_GAP_MS = 5000;

  function reloadCartPage(force) {
    if (!isOnCartPage) return false;
    if (!force) {
      var last = 0;
      try {
        last = parseInt(window.sessionStorage.getItem(REPAINT_KEY), 10) || 0;
      } catch (e) {
        last = 0;
      }
      if (Date.now() - last < REPAINT_MIN_GAP_MS) return false;
    }
    try {
      window.sessionStorage.setItem(REPAINT_KEY, String(Date.now()));
    } catch (e) {}
    hideLoader();
    window.location.reload();
    return true;
  }

  function finalize(newCart) {
    if (reloadCartPage(true)) return newCart;
    hideLoader();
    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: newCart } }));
      // Dawn re-renders the cart drawer / notification bubble off its own
      // pub-sub event (PUB_SUB_EVENTS.cartUpdate), NOT off `cart:updated`
      // which nothing subscribes to. Without this the drawer stays stale
      // after an intercepted qty/remove click on any non-cart page.
      if (
        typeof publish === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS &&
        PUB_SUB_EVENTS.cartUpdate
      ) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-group-sync', cartData: newCart });
      }
    } catch (e) {}
    return newCart;
  }

  function applySingleQuantity(identifier, qty, byIndex) {
    qty = Math.max(0, parseInt(qty, 10) || 0);
    return changeLine(identifier, qty, byIndex)
      .then(function (newCart) {
        refreshFromCart(newCart);
        return finalize(newCart);
      })
      .catch(function (err) {
        console.warn('[Cart Group] Single change failed:', err);
        // The line is already gone (or the cart moved on). Do not leave the
        // customer clicking a dead row — repaint the page.
        if (!reloadCartPage(true)) hideLoader();
        return null;
      });
  }

  // ---------- Identify rows belonging to any group ----------
  function lineIndexForRow(row, element) {
    var el = element || row;
    var idx = el && el.getAttribute ? el.getAttribute('data-index') : null;
    if (!idx && row && row.querySelector) {
      var indexEl = row.querySelector('cart-remove-button[data-index], [data-index]');
      if (indexEl) idx = indexEl.getAttribute('data-index');
    }
    var n = parseInt(idx, 10);
    return isNaN(n) ? null : n;
  }

  // ← UPDATED: prefer the nearest [data-line-key] ancestor. This makes
  // the guard robust against index drift and against clicks on inner
  // SVGs that aren't the keyed element themselves.
  function keyForRow(row, element) {
    var el = element || row;

    if (el && el.closest) {
      var keyed = el.closest('[data-line-key]');
      if (keyed) {
        var k = keyed.getAttribute('data-line-key');
        if (k) return k;
      }
    }

    var key = el && el.getAttribute
      ? (el.getAttribute('data-key') || el.getAttribute('data-line-key'))
      : null;
    if (key) return key;

    var vid = el && el.getAttribute
      ? (el.getAttribute('data-variant-id') || el.getAttribute('data-quantity-variant-id'))
      : null;
    if (vid && variantIdToKey[vid]) return variantIdToKey[vid];

    var line = lineIndexForRow(row, element);
    if (line && indexToKey[line]) return indexToKey[line];
    return null;
  }

  function rowSelector() {
    return '.cart-item, .cart__item, [data-cart-item]';
  }

  function getGroupIdForKey(key) {
    if (!key) return null;
    for (var gid in groups) {
      if (groups[gid].keys.has(key)) return gid;
    }
    return null;
  }

  // ---------- Intercept clicks ----------
  var syncing = false;

  var QTY_BTN_SELECTOR =
    '.quantity__button[name="minus"], .quantity__button[name="plus"], ' +
    '.quantity__button[data-quantity-minus], .quantity__button[data-quantity-plus]';

  var PLUS_BTN_SELECTOR =
    '.quantity__button[name="plus"], .quantity__button[data-quantity-plus]';

  var REMOVE_SELECTOR =
    'cart-remove-button, a[href*="/cart/change"], button[name="remove"], ' +
    '[data-cart-remove], [data-cart-item-remove]';

  function resolveAndApply(key, line, qty) {
    function apply() {
      var resolvedKey = key || (line && indexToKey[line]) || null;
      var gid = resolvedKey ? getGroupIdForKey(resolvedKey) : null;
      if (gid) return applyGroupQuantity(gid, qty);
      if (resolvedKey) return applySingleQuantity(resolvedKey, qty, false);
      if (line) return applySingleQuantity(line, qty, true);
      return Promise.resolve(null);
    }
    if (stateLoaded) return apply();
    return getCart().then(function (cart) {
      refreshFromCart(cart);
      return apply();
    }).catch(function () {
      stateLoaded = true;
      return apply();
    });
  }

  window.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var row = target.closest(rowSelector());
    if (!row) return;

    var isRemove = !!target.closest(REMOVE_SELECTOR);
    var isQtyBtn = !!target.closest(QTY_BTN_SELECTOR);
    if (!isRemove && !isQtyBtn) return;

    // Resolve the line BEFORE swallowing the event. If we cannot tell which
    // line this is, stay out of the way and let the theme's own handler run
    // rather than leaving the click dead.
    var line = lineIndexForRow(row, target);
    var key = keyForRow(row, target);
    if (!key && !line) return;

    // A click that lands while a change is already in flight is ignored — and
    // it must not show the loader first, otherwise the overlay is never taken
    // back down and the whole cart page becomes unclickable.
    if (syncing) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    showLoader();

    var qty;
    if (isRemove) {
      qty = 0;
    } else {
      var input = row.querySelector('.quantity__input');
      var current = parseInt(input && input.value, 10);
      current = isNaN(current) ? 0 : current;
      var isPlus = !!target.closest(PLUS_BTN_SELECTOR);
      qty = isPlus ? current + 1 : Math.max(0, current - 1);
      if (input) input.value = qty;
    }

    syncing = true;
    resolveAndApply(key, line, qty).finally(function () {
      syncing = false;
      hideLoader();
      cleanupOrphans();
    });
  }, true);

  // ---------- Intercept typed quantity changes ----------
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.classList || !target.classList.contains('quantity__input')) return;
    var row = target.closest(rowSelector());
    if (!row) return;

    var qty = parseInt(target.value, 10);
    if (isNaN(qty)) return;

    var line = lineIndexForRow(row, target);
    var key = keyForRow(row, target);
    if (!key && !line) return;

    if (syncing) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    showLoader();

    syncing = true;
    resolveAndApply(key, line, qty).finally(function () {
      syncing = false;
      hideLoader();
      cleanupOrphans();
    });
  }, true);

  // ============================================================
  // ← NEW: BLOCK CHECKOUT UNTIL CLEANUP IS COMPLETE
  // ============================================================
  // Fires in capture phase on every checkout button/link across the
  // site (drawer, cart page, notification popup, floating cart, etc.).
  // If the first cleanup hasn't finished yet, we pause, run it, then
  // re-issue the checkout from the same element.
  // ============================================================
  var CHECKOUT_SELECTOR =
    'button[name="checkout"], input[name="checkout"], ' +
    'a[href$="/checkout"], a[href*="/checkout?"], ' +
    '[data-checkout], .cart__checkout-button';

  window.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var checkoutEl = target.closest(CHECKOUT_SELECTOR);
    if (!checkoutEl) return;

    // Fast path — cleanup already ran this page load.
    if (cleanupComplete) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    showLoader();
    pendingCheckout = checkoutEl;

    cleanupOrphans().finally(function () {
      hideLoader();
      var el = pendingCheckout;
      pendingCheckout = null;
      if (!el) return;

      if (el.tagName === 'A' && el.href) {
        window.location.href = el.href;
      } else if (el.form) {
        // requestSubmit includes the button's name/value (e.g. name="checkout").
        if (typeof el.form.requestSubmit === 'function') {
          el.form.requestSubmit(el);
        } else {
          el.form.submit();
        }
      } else {
        el.click();
      }
    });
  }, true);
  // ============================================================

  // ---------- Keep state up to date ----------
  document.addEventListener('cart:updated', function (event) {
    var cart = event && event.detail && event.detail.cart;
    cleanupComplete = false; // ← NEW: new cart state, guard again
    if (cart) {
      refreshFromCart(cart);
      cleanupOrphans();
    } else {
      setTimeout(function () {
        refreshFromServer().then(cleanupOrphans);
      }, 50);
    }
  });

  document.addEventListener('cart:refresh', function () {
    cleanupComplete = false; // ← NEW
    setTimeout(function () {
      refreshFromServer().then(cleanupOrphans);
    }, 50);
  });

  // ---------- Init ----------
  refreshFromServer().then(function () {
    return cleanupOrphans();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      refreshFromServer().then(function () {
        return cleanupOrphans();
      });
    });
  }

  // ============================================================
  // Public API — used by a PDP add-to-cart flow (Task 291).
  // When a PDP sends the customer straight to /checkout instead of the
  // /cart page, the guard's automatic sweep has already run on page load.
  // A flow that clears and re-fills the cart itself can ask the guard to
  // audit the freshly-changed cart first, so orphan groups, untagged $0
  // component lines and an unauthorised founder gift are removed before
  // checkout is reached.
  // ============================================================
  window.cartGroupGuard = {
    // Re-reads the cart and removes every violating line. Never rejects.
    sync: function () {
      return refreshFromServer().then(cleanupOrphans).catch(function (err) {
        console.warn('[Cart Group] Manual sync failed:', err);
        cleanupComplete = true;
        return null;
      });
    },
    // True once the current cart has been audited this page load.
    isClean: function () {
      return cleanupComplete;
    },
    // Resolves (never rejects) once the current cart has been audited.
    whenClean: function () {
      if (cleanupComplete) return Promise.resolve(true);
      return new Promise(function (resolve) {
        var tries = 0;
        (function poll() {
          if (cleanupComplete || ++tries > 50) return resolve(cleanupComplete);
          setTimeout(poll, 60);
        })();
      });
    }
  };
})();